/**
 * What Numa cost to run, as opposed to what AWS billed the account.
 *
 * The two differ because of quota sharing: one client's model use is often
 * billed to another client's AWS account.
 *   - A borrower's AWS bill shows almost no model spend, though it used plenty.
 *   - A lender's AWS bill shows its own model spend plus every borrower's.
 * The AWS bill also runs about two days behind.
 *
 * So the dashboard takes the model lines out of the AWS bill and puts in the
 * chat cost Numa prices itself from token counts. Every other line of the
 * bill stays as billed.
 *
 *   spend[d]    = bill[d] − model lines[d] + chat cost[d]
 *   lent[d]     = max(0, model lines[d] − billed chat cost[d])
 *   borrowed[d] = max(0, billed chat cost[d] − model lines[d])
 *
 * "Billed" matters because of that two-day delay. On the last day or two the
 * chat cost is already in and the bill is not: the bill's model lines read 0,
 * so the whole day's chat cost looked "borrowed", and a client that shares
 * nothing read as a borrower. A day after the last day an account's bill
 * reaches is "not billed yet". Its chat cost counts in spend (it is all there
 * is for that day), and it counts in neither lent nor borrowed.
 */
import type { DashboardView } from '@/types/fleetAnalytics';
import type { WindowState } from './shared';
import { isAggregate, isModelLineService, modelLinesDaily, sumDailyInWindow } from './shared';

/**
 * The name of the one slice that stands for all model usage in a service
 * breakdown. The same words as the tiles ("Model usage"), so a person sees
 * one thing with one name.
 */
export const INFERRED_LLM_SERVICE_LABEL = 'Model usage';

export type QuotaSharingRole = 'standalone' | 'borrower' | 'lender' | 'mixed';

export interface InferredCostBreakdown {
  /** Model lines on the AWS bill, by day, inside the window. */
  ceClaudeDaily: Record<string, number>;
  /** Token-priced chat cost by day, inside the window. */
  chatDaily: Record<string, number>;
  /** Spend by day: bill − model lines + chat cost. */
  inferredTotalDaily: Record<string, number>;
  /** Model spend billed here for other clients, by day. */
  lentDaily: Record<string, number>;
  /** Model spend billed to another account, by day. */
  borrowedDaily: Record<string, number>;
  /** Chat cost on days AWS has not billed the account for yet, by day. In spend, in neither lent nor borrowed. */
  unbilledDaily: Record<string, number>;
  /** How many days at the end of the window AWS has not billed yet. */
  unbilledDays: number;
  totals: {
    ceTotal: number;
    ceClaude: number;
    chat: number;
    inferred: number;
    lent: number;
    borrowed: number;
    /** Chat cost on days not billed yet. */
    unbilled: number;
    /** inferred − ceTotal. Above zero for a borrower, below for a lender. */
    delta: number;
  };
  role: QuotaSharingRole;
  /**
   * False when the model lines of one account or more cannot be worked out
   * from what is loaded (the per-service breakdown is in the services tier).
   * `inferred` then still holds those accounts' model lines AND their chat
   * cost, so it is too high: load the tier before showing it.
   */
  modelLinesKnown: boolean;
  /**
   * Set when the window starts before the model lines do (an old DynamoDB
   * row keeps 92 days of them). Spend before this day counts model use twice.
   */
  modelLinesFrom?: string;
}

function lastDay(map: Record<string, unknown> | undefined): string | undefined {
  let max: string | undefined;
  for (const d of Object.keys(map || {})) if (!max || d > max) max = d;
  return max;
}

const spendChatCache = new WeakMap<object, Record<string, number>>();

/**
 * Model use priced from tokens by day, as spend counts it. One stack: its own.
 * A summed view: its stacks' model use, plus that of the Inactive stacks it
 * hides. Those stacks are in no use figure, and their money was still spent,
 * so every spend figure takes its model use from here and never from
 * `chat.daily_cost` alone.
 */
export function spendChatDaily(data: DashboardView): Record<string, number> {
  const own = data.chat?.daily_cost ?? {};
  const hidden = isAggregate(data) ? data.inactive_spend?.chat_daily : undefined;
  if (!hidden || !Object.keys(hidden).length) return own;
  const cached = spendChatCache.get(data);
  if (cached) return cached;
  const out: Record<string, number> = { ...own };
  for (const [d, v] of Object.entries(hidden)) out[d] = (out[d] || 0) + (v || 0);
  spendChatCache.set(data, out);
  return out;
}

/**
 * Chat cost by day on the days AWS has not billed yet. One stack: every day
 * after the last day of its bill. A summed view carries the series ready,
 * worked out stack by stack, because each account's bill ends on its own day.
 * A stack with no bill on record at all has no "last billed day", so none of
 * its days count here.
 */
export function unbilledChatDaily(data: DashboardView): Record<string, number> {
  if (isAggregate(data) || data.cost_explorer?.unbilled_chat_daily)
    return data.cost_explorer?.unbilled_chat_daily ?? {};
  const billedTo = lastDay(data.cost_explorer?.totals_by_day);
  const out: Record<string, number> = {};
  if (!billedTo) return out;
  for (const [d, v] of Object.entries(data.chat?.daily_cost || {})) {
    if (d > billedTo && (v || 0) > 0) out[d] = v;
  }
  return out;
}

/** Spend, model lines, lent and borrowed for one view inside the window. */
export function computeInferredCosts(data: DashboardView, win: WindowState): InferredCostBreakdown {
  const ceTotalsByDay = data.cost_explorer?.totals_by_day ?? {};
  const chatDailyRaw = spendChatDaily(data);
  const lines = modelLinesDaily(data.cost_explorer);
  const unknownAccounts = data.portal?.missing?.['cost_explorer.model_lines'] ?? [];
  const from = lines.from ?? data.portal?.model_lines_from;

  const ceClaudeDaily: Record<string, number> = {};
  for (const [d, v] of Object.entries(lines.daily)) {
    if (d >= win.startDate && d <= win.endDate) ceClaudeDaily[d] = v || 0;
  }
  const chatDaily: Record<string, number> = {};
  for (const [d, v] of Object.entries(chatDailyRaw)) {
    if (d >= win.startDate && d <= win.endDate) chatDaily[d] = v || 0;
  }

  const unbilledAll = unbilledChatDaily(data);
  const unbilledDaily: Record<string, number> = {};
  const billedTo = lastDay(ceTotalsByDay);
  let unbilledDays = 0;
  for (const [d, v] of Object.entries(unbilledAll)) {
    if (d < win.startDate || d > win.endDate) continue;
    unbilledDaily[d] = v || 0;
    // Days past the end of the bill as a whole: "the last N days". A day
    // that only one late account has not been billed for is not one of them.
    if (billedTo && d > billedTo) unbilledDays++;
  }

  const inferredTotalDaily: Record<string, number> = {};
  const lentDaily: Record<string, number> = {};
  const borrowedDaily: Record<string, number> = {};
  const allDays = new Set<string>([
    ...Object.keys(ceTotalsByDay).filter((d) => d >= win.startDate && d <= win.endDate),
    ...Object.keys(chatDaily),
  ]);
  for (const d of allDays) {
    const ceTotal = ceTotalsByDay[d] || 0;
    const claudeCE = ceClaudeDaily[d] || 0;
    const chat = chatDaily[d] || 0;
    inferredTotalDaily[d] = ceTotal - claudeCE + chat;
    // Only chat cost AWS has had the chance to bill can be lent or borrowed.
    const billedChat = Math.max(0, chat - (unbilledDaily[d] || 0));
    if (claudeCE > billedChat) lentDaily[d] = claudeCE - billedChat;
    else if (billedChat > claudeCE) borrowedDaily[d] = billedChat - claudeCE;
  }

  const sum = (m: Record<string, number>) => Object.values(m).reduce((a, b) => a + b, 0);
  const totals = {
    ceTotal: sumDailyInWindow(ceTotalsByDay, win),
    ceClaude: sum(ceClaudeDaily),
    chat: sum(chatDaily),
    inferred: sum(inferredTotalDaily),
    lent: sum(lentDaily),
    borrowed: sum(borrowedDaily),
    unbilled: sum(unbilledDaily),
    delta: 0,
  };
  totals.delta = totals.inferred - totals.ceTotal;

  return {
    ceClaudeDaily,
    chatDaily,
    inferredTotalDaily,
    lentDaily,
    borrowedDaily,
    unbilledDaily,
    unbilledDays,
    totals,
    role: classifyRole(totals.lent, totals.borrowed, totals.chat),
    modelLinesKnown: lines.known && unknownAccounts.length === 0,
    modelLinesFrom: from && win.startDate < from ? from : undefined,
  };
}

/**
 * Who pays for whose model use, over the window:
 *   - standalone: lent and borrowed are each under 10% of chat cost
 *   - borrower:   borrowed is 10% of chat cost or more
 *   - lender:     lent is 10% of chat cost or more
 *   - mixed:      both are (quota sharing changed inside the window)
 * The 10% keeps a small daily difference from changing the answer.
 */
function classifyRole(lent: number, borrowed: number, chat: number): QuotaSharingRole {
  if (chat <= 0) return 'standalone';
  const threshold = chat * 0.1;
  const lentSig = lent >= threshold;
  const borrowedSig = borrowed >= threshold;
  if (lentSig && borrowedSig) return 'mixed';
  if (lentSig) return 'lender';
  if (borrowedSig) return 'borrower';
  return 'standalone';
}

/**
 * Spend by AWS service inside the window, with every model line replaced by
 * one slice for token-priced model usage. The slices add up to
 * `computeInferredCosts(...).totals.inferred`. Needs the per-service daily
 * map (services tier): without it the result holds the model slice only.
 */
export function buildInferredServiceMix(data: DashboardView, win: WindowState): Record<string, number> {
  const byServiceDaily = data.cost_explorer?.by_service_daily ?? {};
  const out: Record<string, number> = {};

  for (const [svc, days] of Object.entries(byServiceDaily)) {
    if (isModelLineService(svc)) continue;
    let sum = 0;
    for (const [d, v] of Object.entries(days)) {
      if (d >= win.startDate && d <= win.endDate) sum += v || 0;
    }
    if (sum > 0) out[svc] = sum;
  }

  const chatInWindow = sumDailyInWindow(spendChatDaily(data), win);
  if (chatInWindow > 0) out[INFERRED_LLM_SERVICE_LABEL] = chatInWindow;

  return out;
}
